import {
  normalizeSchoolLevelLabel,
  SCHOOL_LEVELS,
  sectionRequiredForLevel,
} from "@chams-pay/contracts";
import type {
  AuthenticatedUserContract,
  FinancialQueueItemContract,
  LocalFinancialResultContract,
  MessageKey,
  ObligationActionResultContract,
  OperationalPaymentContract,
  SchoolSettingsContract,
  StudentLifecycleStatusContract,
  StudentEditDetailsContract,
  StudentMutationContract,
  StudentProfileContract,
  StudentReplicaContract,
  StudentSearchItemContract,
  StudentSearchResultContract,
  StudentServiceAmountsResultContract,
} from "@chams-pay/contracts";
import { useCallback, useEffect, useRef, useState } from "react";
import { deviceFetch, json, localStore } from "./client.js";

const SEARCH_DEBOUNCE_MS = 250;

export type StudentPaymentFilter =
  "all" | "up-to-date" | "one-month-late" | "two-months-late" | "three-plus-months-late";
export type StudentLifecycleFilter = "all" | StudentLifecycleStatusContract;
export type StudentArrearsFilter =
  "overdue" | "one" | "two" | "three-plus" | "urgent" | "registration" | null;

export interface StudentFilters {
  readonly classLabel: string | null;
  readonly lifecycleStatus: StudentLifecycleFilter;
  readonly paymentStatus: StudentPaymentFilter;
  readonly arrears: StudentArrearsFilter;
  readonly arrearsAsOf: string | null;
  readonly arrearsAcademicYear: string | null;
}

export const DEFAULT_STUDENT_FILTERS: StudentFilters = {
  classLabel: null,
  lifecycleStatus: "all",
  paymentStatus: "all",
  arrears: null,
  arrearsAsOf: null,
  arrearsAcademicYear: null,
};

export function arrearsStudentFilters(
  arrears: Exclude<StudentArrearsFilter, null>,
  arrearsAsOf: string,
  arrearsAcademicYear?: string,
): StudentFilters {
  return {
    ...DEFAULT_STUDENT_FILTERS,
    paymentStatus: "all",
    arrears,
    arrearsAsOf,
    arrearsAcademicYear: arrearsAcademicYear ?? null,
  };
}

export function obligationOverdueAt(
  obligation: StudentProfileContract["obligations"][number],
  asOf: string,
): boolean {
  if (obligation.family !== "registration") return obligation.dueDate < asOf;
  const match = /^(\d{4})-(\d{4})$/.exec(obligation.period);
  return match !== null && asOf >= `${match[1]}-10-01`;
}

function unpaidOverdueObligations(profile: StudentProfileContract, asOf: string | null) {
  return profile.obligations.filter(
    (obligation) =>
      obligation.remainingMad !== "0" &&
      (asOf ? obligationOverdueAt(obligation, asOf) : obligation.overdue),
  );
}

function recurringArrearsMonths(profile: StudentProfileContract, asOf: string | null): number {
  return new Set(
    unpaidOverdueObligations(profile, asOf)
      .filter((obligation) => ["tuition", "transport", "accompaniment"].includes(obligation.family))
      .map((obligation) => obligation.period),
  ).size;
}

function unpaidOverdueRegistration(
  profile: StudentProfileContract,
  asOf: string | null,
  academicYear: string | null,
) {
  if (!asOf || !academicYear) return [];
  return profile.obligations.filter(
    (obligation) =>
      obligation.family === "registration" &&
      obligation.period === academicYear &&
      obligation.remainingMad !== "0" &&
      obligationOverdueAt(obligation, asOf),
  );
}

export function matchesFilters(
  item: StudentSearchItemContract,
  profile: StudentProfileContract | undefined,
  filters: StudentFilters,
): boolean {
  if (filters.classLabel && normalizeSchoolLevelLabel(item.currentClass) !== filters.classLabel)
    return false;
  if (filters.lifecycleStatus !== "all" && item.lifecycleStatus !== filters.lifecycleStatus)
    return false;
  if (!profile) return filters.paymentStatus === "all" && filters.arrears === null;

  const overdue = unpaidOverdueObligations(profile, filters.arrearsAsOf).length > 0;
  const arrearsMonths = recurringArrearsMonths(profile, filters.arrearsAsOf);
  if (filters.paymentStatus === "up-to-date" && overdue) return false;
  if (filters.paymentStatus === "one-month-late" && arrearsMonths !== 1) return false;
  if (filters.paymentStatus === "two-months-late" && arrearsMonths !== 2) return false;
  if (filters.paymentStatus === "three-plus-months-late" && arrearsMonths < 3) return false;

  if (filters.arrears) {
    const months = arrearsMonths;
    if (filters.arrears === "overdue" && !overdue) return false;
    if (filters.arrears === "one" && months !== 1) return false;
    if (filters.arrears === "two" && months !== 2) return false;
    if (filters.arrears === "three-plus" && months < 3) return false;
    if (filters.arrears === "urgent" && months < 2) return false;
    if (
      filters.arrears === "registration" &&
      unpaidOverdueRegistration(profile, filters.arrearsAsOf, filters.arrearsAcademicYear)
        .length === 0
    )
      return false;
  }
  return true;
}

function overdueAmount(
  profile: StudentProfileContract | undefined,
  filters: StudentFilters,
): bigint {
  if (!profile) return 0n;
  const obligations =
    filters.arrears === "registration"
      ? unpaidOverdueRegistration(profile, filters.arrearsAsOf, filters.arrearsAcademicYear)
      : unpaidOverdueObligations(profile, filters.arrearsAsOf);
  return obligations.reduce((total, obligation) => total + BigInt(obligation.remainingMad), 0n);
}

export function orderedStudents(
  students: readonly StudentSearchItemContract[],
  profiles: readonly StudentProfileContract[] = [],
  filters: StudentFilters = DEFAULT_STUDENT_FILTERS,
): readonly StudentSearchItemContract[] {
  const byId = new Map(profiles.map((profile) => [profile.id, profile] as const));
  const orderByArrears = filters.paymentStatus !== "all" || filters.arrears !== null;
  return [...students].sort((left, right) => {
    if (orderByArrears) {
      const difference =
        overdueAmount(byId.get(right.id), filters) - overdueAmount(byId.get(left.id), filters);
      if (difference !== 0n) return difference > 0n ? 1 : -1;
    }
    return Number(left.lifecycleStatus !== "active") - Number(right.lifecycleStatus !== "active");
  });
}

export interface StudentSearchController {
  readonly query: string;
  readonly items: readonly StudentSearchItemContract[];
  readonly activeCount: number;
  readonly busy: boolean;
  readonly filters: StudentFilters;
  readonly filtersActive: boolean;
  readonly setQuery: (value: string) => void;
  readonly setFilters: (filters: StudentFilters) => void;
  readonly clearFilters: () => void;
  readonly searchNow: (value: string) => void;
  readonly openStudent: (id: string) => Promise<void>;
  readonly selected: StudentProfileContract | null;
  readonly clearSelection: () => void;
  readonly studentSaved: (profile: StudentProfileContract) => void;
  readonly applyObligationAction: (result: ObligationActionResultContract) => void;
  readonly applyServiceAmounts: (result: StudentServiceAmountsResultContract) => void;
  readonly refreshSelected: () => Promise<void>;
  readonly localHistory: readonly LocalFinancialResultContract[];
  readonly serverHistory: readonly OperationalPaymentContract[];
  readonly unsynchronizedHistory: readonly LocalFinancialResultContract[];
  readonly reloadLocalHistory: () => Promise<void>;
  /** Only a financial administrator may capture money. */
  readonly paymentActorId: string | null;
}

export function useStudents({
  online,
  user,
  replica,
  queue,
  synchronizing,
}: {
  readonly online: boolean;
  readonly user: AuthenticatedUserContract | null;
  readonly replica: StudentReplicaContract;
  readonly queue: readonly FinancialQueueItemContract[];
  readonly synchronizing: boolean;
}): StudentSearchController {
  const [query, setQuery] = useState("");
  const [allItems, setAllItems] = useState<readonly StudentSearchItemContract[]>(() =>
    orderedStudents(replica.students, replica.students),
  );
  const [items, setItems] = useState<readonly StudentSearchItemContract[]>(() =>
    orderedStudents(replica.students, replica.students),
  );
  const [selected, setSelected] = useState<StudentProfileContract | null>(null);
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState<StudentFilters>(DEFAULT_STUDENT_FILTERS);
  const [localHistory, setLocalHistory] = useState<readonly LocalFinancialResultContract[]>([]);
  const [serverHistory, setServerHistory] = useState<readonly OperationalPaymentContract[]>([]);
  const searchTimer = useRef<number | null>(null);
  const searchAbort = useRef<AbortController | null>(null);

  useEffect(() => {
    const ordered = orderedStudents(replica.students, replica.students);
    setAllItems(ordered);
    setItems(ordered);
  }, [replica]);

  useEffect(() => {
    if (!selected) {
      setLocalHistory([]);
      setServerHistory([]);
      return;
    }
    // Server profile and history requests can straddle a payment commit. Keep
    // the previous complete snapshot until the synchronization run finishes.
    if (synchronizing) return;
    let cancelled = false;
    const selectedId = selected.id;
    const localSnapshot = localStore
      .finalizedPayments(selectedId)
      .catch(() => [] as readonly LocalFinancialResultContract[]);
    const serverSnapshot =
      online && user
        ? Promise.all([
            deviceFetch(`/api/students/${selectedId}`).then((response) =>
              json<StudentProfileContract>(response),
            ),
            deviceFetch(`/api/operations/students/${selectedId}/payments`).then((response) =>
              json<{ items: readonly OperationalPaymentContract[] }>(response),
            ),
          ])
            .then(([profile, result]) => ({
              status: "ready" as const,
              profile,
              history: result.items,
            }))
            .catch(() => ({ status: "failed" as const }))
        : Promise.resolve({ status: "offline" as const });

    void Promise.all([localSnapshot, serverSnapshot]).then(([local, server]) => {
      if (cancelled || server.status === "failed") return;
      if (server.status === "ready")
        setSelected((current) => (current?.id === selectedId ? server.profile : current));
      setLocalHistory(local);
      setServerHistory(server.status === "ready" ? server.history : []);
    });

    return () => {
      cancelled = true;
    };
  }, [online, queue, selected?.id, synchronizing, user]);

  const performSearch = useCallback(
    async (value: string, controller: AbortController) => {
      try {
        if (online && user) {
          const result = await json<StudentSearchResultContract>(
            await deviceFetch(`/api/students?q=${encodeURIComponent(value)}&page=1&pageSize=20`, {
              signal: controller.signal,
            }),
          );
          if (!controller.signal.aborted)
            setItems(
              orderedStudents(
                result.items.filter((item) =>
                  matchesFilters(
                    item,
                    replica.students.find((profile) => profile.id === item.id),
                    filters,
                  ),
                ),
                replica.students,
                filters,
              ),
            );
        } else if (!controller.signal.aborted)
          setItems(
            orderedStudents(
              localStore.search(value).filter((item) =>
                matchesFilters(
                  item,
                  replica.students.find((profile) => profile.id === item.id),
                  filters,
                ),
              ),
              replica.students,
              filters,
            ),
          );
      } catch {
        if (!controller.signal.aborted)
          setItems(
            orderedStudents(
              localStore.search(value).filter((item) =>
                matchesFilters(
                  item,
                  replica.students.find((profile) => profile.id === item.id),
                  filters,
                ),
              ),
              replica.students,
              filters,
            ),
          );
      } finally {
        if (!controller.signal.aborted) setBusy(false);
      }
    },
    [filters, online, replica.students, user],
  );

  const searchNow = useCallback(
    (value: string) => {
      if (searchTimer.current) window.clearTimeout(searchTimer.current);
      searchAbort.current?.abort();
      const normalized = value.trim();
      if (!normalized) {
        setItems(
          orderedStudents(
            allItems.filter((item) =>
              matchesFilters(
                item,
                replica.students.find((profile) => profile.id === item.id),
                filters,
              ),
            ),
            replica.students,
            filters,
          ),
        );
        setBusy(false);
        return;
      }
      const controller = new AbortController();
      searchAbort.current = controller;
      setBusy(true);
      void performSearch(normalized, controller);
    },
    [allItems, filters, performSearch, replica.students],
  );

  useEffect(() => {
    if (searchTimer.current) window.clearTimeout(searchTimer.current);
    searchAbort.current?.abort();
    const normalized = query.trim();
    if (!normalized) {
      setItems(
        orderedStudents(
          allItems.filter((item) =>
            matchesFilters(
              item,
              replica.students.find((profile) => profile.id === item.id),
              filters,
            ),
          ),
          replica.students,
          filters,
        ),
      );
      setBusy(false);
      return;
    }
    setBusy(true);
    searchTimer.current = window.setTimeout(() => searchNow(normalized), SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchTimer.current) window.clearTimeout(searchTimer.current);
      searchAbort.current?.abort();
    };
  }, [allItems, filters, query, replica.students, searchNow]);

  const openStudent = useCallback(
    async (id: string) => {
      setBusy(true);
      try {
        setSelected(
          online && user && !synchronizing
            ? await json<StudentProfileContract>(await deviceFetch(`/api/students/${id}`))
            : localStore.profile(id),
        );
      } catch {
        setSelected(localStore.profile(id));
      } finally {
        setBusy(false);
      }
    },
    [online, synchronizing, user],
  );

  const refreshSelected = useCallback(async () => {
    if (!selected || !online || !user || synchronizing) return;
    const selectedId = selected.id;
    const [profile, payments, local] = await Promise.all([
      json<StudentProfileContract>(await deviceFetch(`/api/students/${selected.id}`)),
      json<{ items: readonly OperationalPaymentContract[] }>(
        await deviceFetch(`/api/operations/students/${selected.id}/payments`),
      ),
      localStore.finalizedPayments(selected.id),
    ]);
    setSelected((current) => (current?.id === selectedId ? profile : current));
    setServerHistory(payments.items);
    setLocalHistory(local);
  }, [online, selected, synchronizing, user]);

  const studentSaved = useCallback((profile: StudentProfileContract) => {
    const item: StudentSearchItemContract = {
      id: profile.id,
      internalReference: profile.internalReference,
      massar: profile.massar,
      nameAr: profile.nameAr,
      nameFr: profile.nameFr,
      currentClass: profile.currentClass,
      sectionLabel: profile.sectionLabel,
      serviceSummaryAr: profile.serviceSummaryAr,
      serviceSummaryFr: profile.serviceSummaryFr,
      lifecycleStatus: profile.lifecycleStatus,
    };
    setAllItems((current) =>
      orderedStudents([item, ...current.filter((entry) => entry.id !== item.id)]),
    );
    setItems((current) =>
      orderedStudents([item, ...current.filter((entry) => entry.id !== item.id)]),
    );
    setSelected(profile);
  }, []);

  const applyObligationAction = useCallback((result: ObligationActionResultContract) => {
    setSelected((current) => {
      if (!current) return current;
      const obligations = current.obligations.map((obligation) =>
        obligation.id === result.obligation.id ? result.obligation : obligation,
      );
      const currentObligations = obligations.filter(
        (obligation) => obligation.calendarSnapshotId === current.calendar.id,
      );
      return {
        ...current,
        obligations,
        obligationSummary: {
          currentYearTotalMad: currentObligations
            .reduce((sum, obligation) => sum + BigInt(obligation.netMad), 0n)
            .toString(),
          outstandingMad: currentObligations
            .reduce((sum, obligation) => sum + BigInt(obligation.remainingMad), 0n)
            .toString(),
          overdueCount: currentObligations.filter((obligation) => obligation.overdue).length,
          readOnly: true,
        },
      };
    });
  }, []);

  const applyServiceAmounts = useCallback((result: StudentServiceAmountsResultContract) => {
    setSelected((current) => {
      if (!current) return current;
      const updatedById = new Map(
        result.obligations.map((obligation) => [obligation.id, obligation]),
      );
      const obligations = current.obligations.map(
        (obligation) => updatedById.get(obligation.id) ?? obligation,
      );
      const currentObligations = obligations.filter(
        (obligation) => obligation.calendarSnapshotId === current.calendar.id,
      );
      return {
        ...current,
        obligations,
        obligationSummary: {
          currentYearTotalMad: currentObligations
            .reduce((sum, obligation) => sum + BigInt(obligation.netMad), 0n)
            .toString(),
          outstandingMad: currentObligations
            .reduce((sum, obligation) => sum + BigInt(obligation.remainingMad), 0n)
            .toString(),
          overdueCount: currentObligations.filter((obligation) => obligation.overdue).length,
          readOnly: true,
        },
      };
    });
  }, []);

  const reloadLocalHistory = useCallback(async () => {
    if (!selected) return;
    setLocalHistory(await localStore.finalizedPayments(selected.id));
  }, [selected]);

  const unsynchronizedHistory = localHistory.filter(
    (entry) =>
      entry.queueState !== "synced" &&
      !serverHistory.some((server) => server.payment.id === entry.payment.id),
  );

  const paymentActorId =
    user?.role === "financial-administrator"
      ? user.id
      : replica.authorizedActor?.role === "financial-administrator"
        ? replica.authorizedActor.id
        : null;

  return {
    query,
    items,
    activeCount: allItems.filter((entry) => entry.lifecycleStatus === "active").length,
    busy,
    filters,
    filtersActive:
      filters.classLabel !== null ||
      filters.lifecycleStatus !== "all" ||
      filters.paymentStatus !== "all" ||
      filters.arrears !== null,
    setQuery,
    setFilters,
    clearFilters: useCallback(() => setFilters(DEFAULT_STUDENT_FILTERS), []),
    searchNow,
    openStudent,
    selected,
    clearSelection: useCallback(() => setSelected(null), []),
    studentSaved,
    applyObligationAction,
    applyServiceAmounts,
    refreshSelected,
    localHistory,
    serverHistory,
    unsynchronizedHistory,
    reloadLocalHistory,
    paymentActorId,
  };
}

/* ------------------------------------------------------------------ */
/* Student registration and editing                                    */
/* ------------------------------------------------------------------ */

export type StudentFormError = "duplicate" | "failed" | null;
export type StudentValidatedField = "massar" | "birthDate" | "guardianPhone";

/** Reads the machine-readable failure code the API returns, if it sent one. */
async function failureCode(response: Response): Promise<string> {
  try {
    const body: unknown = await response.clone().json();
    if (body && typeof body === "object" && "code" in body) {
      const code = (body as { readonly code?: unknown }).code;
      if (typeof code === "string" && code) return code;
    }
  } catch {
    /* A non-JSON failure still deserves a readable code. */
  }
  return `HTTP_${response.status}`;
}

export interface StudentFormController {
  readonly step: 1 | 2;
  readonly value: StudentMutationContract;
  readonly busy: boolean;
  readonly detailsBusy: boolean;
  readonly error: StudentFormError;
  /** The failure code the API reported, surfaced as a chip beside the message. */
  readonly errorCode: string | null;
  readonly fieldErrors: Partial<Record<StudentValidatedField, true>>;
  readonly identityValid: boolean;
  readonly enrollmentValid: boolean;
  readonly sectionRequired: boolean;
  readonly sectionOptions: readonly ("A" | "B")[];
  readonly editing: boolean;
  readonly monthlyAmountsMad: StudentFormMonthlyAmounts;
  readonly computedMonthlyMad: string | null;
  readonly registrationYears: readonly {
    readonly academicYear: string;
    readonly role: "current" | "next";
  }[];
  /** A student already carrying the typed MASSAR, found through search. */
  readonly duplicate: StudentSearchItemContract | null;
  readonly duplicateBusy: boolean;
  readonly patch: (patch: Partial<StudentMutationContract>) => void;
  readonly blurField: (field: StudentValidatedField) => void;
  readonly setStep: (step: 1 | 2) => void;
  readonly checkDuplicate: () => Promise<void>;
  readonly dismissDuplicate: () => void;
  readonly clearError: () => void;
  readonly save: () => Promise<void>;
}

const emptyStudent: StudentMutationContract = {
  nameAr: "",
  nameFr: "",
  massar: null,
  birthDate: "",
  guardianName: "",
  guardianPhone: "",
  classLabel: "",
  sectionLabel: null,
  transport: false,
  accompaniment: false,
};

type StudentFormMonthlyFamily = "tuition" | "transport" | "accompaniment";
export type StudentFormMonthlyAmounts = Partial<Record<StudentFormMonthlyFamily, string>>;

function studentMonthlyAmounts(
  student: StudentProfileContract,
  academicYear: string,
): StudentFormMonthlyAmounts {
  const calendarSnapshotId =
    student.academicYears?.find((year) => year.academicYear === academicYear)?.calendarSnapshotId ??
    (student.calendar.academicYear === academicYear ? student.calendar.id : null);
  if (!calendarSnapshotId) return {};
  const monthly = student.obligations.filter(
    (obligation) =>
      obligation.calendarSnapshotId === calendarSnapshotId &&
      obligation.family !== "registration" &&
      /^\d{4}-\d{2}$/.test(obligation.period),
  );
  const reference = monthly
    .map((obligation) => obligation.period)
    .sort()
    .at(-1);
  if (!reference) return {};
  return Object.fromEntries(
    monthly
      .filter((obligation) => obligation.period === reference)
      .map((obligation) => [obligation.family, obligation.selectionMad ?? obligation.netMad]),
  ) as StudentFormMonthlyAmounts;
}

export function studentFormMonthlyAmounts(
  settings: SchoolSettingsContract | null,
  value: StudentMutationContract,
  student?: StudentProfileContract,
): StudentFormMonthlyAmounts {
  const academicYear =
    value.academicYear ?? student?.enrollment.academicYear ?? settings?.academicYear;
  const amounts: StudentFormMonthlyAmounts = {};
  if (settings && academicYear) {
    const configuredAmounts =
      academicYear === settings.advanceYear?.academicYear
        ? settings.advanceYear.amountsMad
        : settings.amountsMad;
    amounts.transport = configuredAmounts.transport;
    amounts.accompaniment = configuredAmounts.accompaniment;
    const configuredClass = settings.classes.find(
      (entry) => entry.label === normalizeSchoolLevelLabel(value.classLabel),
    );
    if (configuredClass)
      amounts.tuition =
        (configuredClass.tuitionTariff ?? configuredClass.cycle) === "preschool"
          ? configuredAmounts.preschoolTuition
          : configuredAmounts.primaryTuition;
  }
  if (student && academicYear) Object.assign(amounts, studentMonthlyAmounts(student, academicYear));
  return amounts;
}

export function studentFormSectionOptions(
  settings: SchoolSettingsContract | null,
  classLabel: string,
  classOptions: readonly string[] = [],
): readonly ("A" | "B")[] {
  const chamsProfile = settings
    ? sectionRequiredForLevel(settings.classes, classLabel)
    : classOptions.length === SCHOOL_LEVELS.length &&
      classOptions.every((label, index) => label === SCHOOL_LEVELS[index]?.label) &&
      classLabel !== "PS" &&
      SCHOOL_LEVELS.some((entry) => entry.label === classLabel);
  return chamsProfile ? ["A", "B"] : [];
}

export function studentFormEnrollmentValid(
  settings: SchoolSettingsContract | null,
  value: Pick<StudentMutationContract, "classLabel" | "sectionLabel">,
  classOptions: readonly string[] = [],
): boolean {
  if (!value.classLabel.trim()) return false;
  const options = studentFormSectionOptions(settings, value.classLabel, classOptions);
  return options.length
    ? options.includes(value.sectionLabel as "A" | "B")
    : value.sectionLabel === null;
}

export function computedStudentMonthlyMad(
  amounts: StudentFormMonthlyAmounts,
  value: Pick<StudentMutationContract, "transport" | "accompaniment">,
): string | null {
  if (!amounts.tuition || !/^\d+$/.test(amounts.tuition)) return null;
  return (
    BigInt(amounts.tuition) +
    (value.transport && amounts.transport && /^\d+$/.test(amounts.transport)
      ? BigInt(amounts.transport)
      : 0n) +
    (value.accompaniment && amounts.accompaniment && /^\d+$/.test(amounts.accompaniment)
      ? BigInt(amounts.accompaniment)
      : 0n)
  ).toString();
}

export function studentFormErrorBodyKey(
  error: StudentFormError,
  errorCode: string | null,
): MessageKey {
  if (error === "duplicate") return "sp.dupBody";
  if (errorCode === "INVALID_STUDENT") return "sp.invalidStudentSaveBody";
  if (errorCode === "STUDENT_SAVE_FAILED") return "sp.studentServiceSaveFailedBody";
  return "sp.saveFailBody";
}

function normalizedMassar(value: string): string {
  return value.normalize("NFKC").toUpperCase().replace(/[\s-]/g, "");
}

function normalizedGuardianPhone(value: string): string | null {
  const westernDigits = value
    .normalize("NFKC")
    .replace(/[٠-٩]/g, (digit) => String("٠١٢٣٤٥٦٧٨٩".indexOf(digit)))
    .replace(/[۰-۹]/g, (digit) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(digit)))
    .trim();
  if (!westernDigits || !/^\+?[0-9\s().-]+$/.test(westernDigits)) return null;
  let compact = westernDigits.replace(/[\s().-]/g, "");
  if (compact.startsWith("00")) compact = `+${compact.slice(2)}`;
  if (!compact.startsWith("+")) {
    if (/^0[5-7][0-9]{8}$/.test(compact)) compact = `+212${compact.slice(1)}`;
    else if (/^[5-7][0-9]{8}$/.test(compact)) compact = `+212${compact}`;
    else return null;
  }
  if (compact.startsWith("+2120")) compact = `+212${compact.slice(5)}`;
  return /^\+[1-9][0-9]{7,14}$/.test(compact) ? compact : null;
}

function validBirthDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return (
    !Number.isNaN(date.valueOf()) && date.toISOString().slice(0, 10) === value && date <= new Date()
  );
}

export function studentFormBirthDateValid(
  value: string,
  existingBirthDate?: string | null,
): boolean {
  return validBirthDate(value) || (existingBirthDate === null && value === "");
}

export function useStudentForm({
  open,
  csrf,
  student,
  classOptions = [],
  onSaved,
  onClose,
}: {
  readonly open: boolean;
  readonly csrf: string;
  readonly student?: StudentProfileContract | undefined;
  readonly classOptions?: readonly string[];
  readonly onSaved: (profile: StudentProfileContract) => void;
  readonly onClose: () => void;
}): StudentFormController {
  const [step, setStep] = useState<1 | 2>(1);
  const [busy, setBusy] = useState(false);
  const [detailsBusy, setDetailsBusy] = useState(false);
  const [error, setError] = useState<StudentFormError>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<StudentSearchItemContract | null>(null);
  const [duplicateBusy, setDuplicateBusy] = useState(false);
  const [value, setValue] = useState<StudentMutationContract>(emptyStudent);
  const [schoolSettings, setSchoolSettings] = useState<SchoolSettingsContract | null>(null);
  const [registrationYears, setRegistrationYears] = useState<
    StudentFormController["registrationYears"]
  >([]);
  const [touchedFields, setTouchedFields] = useState<ReadonlySet<StudentValidatedField>>(new Set());

  useEffect(() => {
    if (!open) {
      setDetailsBusy(false);
      return;
    }
    const summaries = `${student?.serviceSummaryAr ?? ""} ${student?.serviceSummaryFr ?? ""}`;
    setValue({
      nameAr: student?.nameAr ?? "",
      nameFr: student?.nameFr ?? "",
      massar: student?.massar ?? null,
      birthDate: student?.birthDate ?? "",
      guardianName: student?.primaryGuardian.name ?? "",
      guardianPhone: "",
      classLabel: student?.currentClass ?? "",
      sectionLabel: student?.sectionLabel ?? null,
      transport: /النقل|transport/i.test(summaries),
      accompaniment: /المواكبة|المرافقة|accompagnement/i.test(summaries),
      ...(student ? { academicYear: student.enrollment.academicYear } : {}),
    });
    setStep(1);
    setError(null);
    setErrorCode(null);
    setDuplicate(null);
    setTouchedFields(new Set());
    if (!student) {
      setDetailsBusy(false);
      return;
    }
    let active = true;
    setDetailsBusy(true);
    void deviceFetch(`/api/admin/students/${student.id}/edit-details`)
      .then((response) => json<StudentEditDetailsContract>(response))
      .then((details) => {
        if (active) setValue((current) => ({ ...current, guardianPhone: details.guardianPhone }));
      })
      .catch(() => {
        if (!active) return;
        setError("failed");
        setErrorCode("STUDENT_EDIT_DETAILS_FAILED");
      })
      .finally(() => {
        if (active) setDetailsBusy(false);
      });
    return () => {
      active = false;
    };
  }, [open, student]);

  useEffect(() => {
    if (
      schoolSettings &&
      studentFormSectionOptions(schoolSettings, value.classLabel, classOptions).length === 0
    )
      setValue((current) =>
        current.sectionLabel === null ? current : { ...current, sectionLabel: null },
      );
  }, [classOptions, schoolSettings, value.classLabel]);

  useEffect(() => {
    if (!open) {
      setRegistrationYears([]);
      setSchoolSettings(null);
      return;
    }
    if (student) setRegistrationYears([]);
    let active = true;
    void deviceFetch("/api/admin/school-settings")
      .then((response) => json<SchoolSettingsContract>(response))
      .then((settings) => {
        if (!active) return;
        setSchoolSettings(settings);
        if (student) return;
        const years: StudentFormController["registrationYears"] = [
          { academicYear: settings.academicYear, role: "current" },
          ...(settings.advanceYear
            ? [{ academicYear: settings.advanceYear.academicYear, role: "next" as const }]
            : []),
        ];
        setRegistrationYears(years);
        setValue((current) => ({
          ...current,
          academicYear: current.academicYear ?? settings.academicYear,
        }));
      })
      .catch(() => {
        setSchoolSettings(null);
        if (!student) setRegistrationYears([]);
      });
    return () => {
      active = false;
    };
  }, [open, student]);

  /**
   * Reuses the ordinary student search to warn about a MASSAR already in use.
   * It is advisory only — the server stays the authority and still rejects a
   * genuine duplicate on save.
   */
  const checkDuplicate = useCallback(async () => {
    const massar = value.massar ? normalizedMassar(value.massar) : "";
    if (massar.length < 6) {
      setDuplicate(null);
      return;
    }
    setDuplicateBusy(true);
    try {
      const result = await json<StudentSearchResultContract>(
        await deviceFetch(`/api/students?q=${encodeURIComponent(massar)}&page=1&pageSize=5`),
      );
      setDuplicate(
        result.items.find(
          (item) =>
            (item.massar ? normalizedMassar(item.massar) : null) === massar &&
            item.id !== student?.id,
        ) ?? null,
      );
    } catch {
      setDuplicate(null);
    } finally {
      setDuplicateBusy(false);
    }
  }, [student?.id, value.massar]);

  const massarValid = !value.massar || /^[A-Z0-9]{6,20}$/.test(normalizedMassar(value.massar));
  const normalizedPhone = value.guardianPhone?.trim()
    ? normalizedGuardianPhone(value.guardianPhone)
    : null;
  const phoneValid = normalizedPhone !== null;
  const birthDateValid = studentFormBirthDateValid(value.birthDate, student?.birthDate);
  const identityValid =
    !detailsBusy &&
    value.nameAr.trim().length >= 2 &&
    value.nameFr.trim().length >= 2 &&
    value.guardianName.trim().length >= 2 &&
    birthDateValid &&
    massarValid &&
    phoneValid;
  const sectionOptions = studentFormSectionOptions(schoolSettings, value.classLabel, classOptions);
  const sectionRequired = sectionOptions.length > 0;
  const enrollmentValid = studentFormEnrollmentValid(schoolSettings, value, classOptions);
  const monthlyAmountsMad = studentFormMonthlyAmounts(schoolSettings, value, student);
  const computedMonthlyMad = computedStudentMonthlyMad(monthlyAmountsMad, value);
  const fieldErrors: Partial<Record<StudentValidatedField, true>> = {
    ...(touchedFields.has("massar") && !massarValid ? { massar: true as const } : {}),
    ...(touchedFields.has("birthDate") && !birthDateValid ? { birthDate: true as const } : {}),
    ...(touchedFields.has("guardianPhone") && !phoneValid ? { guardianPhone: true as const } : {}),
  };

  const save = useCallback(async () => {
    if (!identityValid || !enrollmentValid) {
      setTouchedFields(new Set(["massar", "birthDate", "guardianPhone"]));
      setError("failed");
      setErrorCode(null);
      return;
    }
    setBusy(true);
    setError(null);
    setErrorCode(null);
    try {
      const url = student ? `/api/admin/students/${student.id}` : "/api/admin/students";
      const response = await deviceFetch(url, {
        method: student ? "PUT" : "POST",
        headers: { "content-type": "application/json", "x-csrf-token": csrf },
        body: JSON.stringify({
          ...value,
          massar: value.massar ? normalizedMassar(value.massar) : null,
          guardianPhone: normalizedPhone ?? value.guardianPhone,
        }),
      });
      if (response.status === 409) {
        setError("duplicate");
        setErrorCode(await failureCode(response));
        return;
      }
      if (!response.ok) {
        setError("failed");
        setErrorCode(await failureCode(response));
        return;
      }
      onSaved((await response.json()) as StudentProfileContract);
      onClose();
    } catch {
      setError("failed");
    } finally {
      setBusy(false);
    }
  }, [csrf, enrollmentValid, identityValid, normalizedPhone, onClose, onSaved, student, value]);

  return {
    step,
    value,
    busy,
    detailsBusy,
    error,
    errorCode,
    fieldErrors,
    identityValid,
    enrollmentValid,
    sectionRequired,
    sectionOptions,
    editing: Boolean(student),
    monthlyAmountsMad,
    computedMonthlyMad,
    registrationYears,
    duplicate,
    duplicateBusy,
    checkDuplicate,
    dismissDuplicate: useCallback(() => setDuplicate(null), []),
    clearError: useCallback(() => {
      setError(null);
      setErrorCode(null);
    }, []),
    patch: useCallback(
      (patch: Partial<StudentMutationContract>) =>
        setValue((current) => {
          const next = { ...current, ...patch };
          return patch.classLabel !== undefined &&
            studentFormSectionOptions(schoolSettings, next.classLabel, classOptions).length === 0
            ? { ...next, sectionLabel: null }
            : next;
        }),
      [classOptions, schoolSettings],
    ),
    blurField: useCallback((field: StudentValidatedField) => {
      setTouchedFields((current) => new Set([...current, field]));
      setValue((current) => {
        if (field === "massar" && current.massar)
          return { ...current, massar: normalizedMassar(current.massar) };
        if (field === "guardianPhone" && current.guardianPhone) {
          const normalized = normalizedGuardianPhone(current.guardianPhone);
          if (normalized) return { ...current, guardianPhone: normalized };
        }
        return current;
      });
    }, []),
    setStep,
    save,
  };
}
